import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Divider,
  Flex,
  ModalAction,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalV2,
  Text,
} from '@electricjs/arc';

import { ApplicationSelector } from '@/components/Applications/AddApplication/ApplicationSelector';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGetOrganizationApplicationsQuery } from '@/redux/slices/organizationApiSlice';
import { Application } from '@/types/applications';

type AddOnboardingAppModalProps = {
  closeModal: () => void;
  onAdd: (apps: Application[]) => void;
  excludedAppIds: Set<string>;
};

const AddOnboardingAppModal = ({
  closeModal,
  onAdd,
  excludedAppIds,
}: AddOnboardingAppModalProps) => {
  const { t } = useTranslation('common');
  const organizationId = useGetOrganizationId();

  const { data: orgAppsData, isFetching } = useGetOrganizationApplicationsQuery(
    { organizationId, limit: 1000 }
  );

  const [selectedApps, setSelectedApps] = useState<Application[]>([]);

  const options =
    orgAppsData?.applications?.map(orgApp => ({
      ...orgApp.application,
      isDisabled:
        excludedAppIds.has(orgApp.application.id) ||
        !!orgApp.disableCreateAccountRequests,
    })) ?? [];

  const handleAdd = () => {
    onAdd(selectedApps);
    closeModal();
  };

  return (
    <ModalV2 visible hide={closeModal} ariaLabelledby="add-onboarding-app">
      <ModalHeader>
        <Text variant="heading-2">
          {t('onboardings.addOnboardingAppModal.title')}
        </Text>
      </ModalHeader>
      <Divider />
      <ModalBody>
        <Flex flexDirection="column" gap="1.6rem">
          <Text variant="body">
            {t('onboardings.addOnboardingAppModal.description')}
          </Text>
          <ApplicationSelector
            isMulti
            options={options}
            isFetching={isFetching}
            applicationSelected={selectedApps}
            onApplicationSelectedChange={setSelectedApps}
          />
        </Flex>
      </ModalBody>
      <Divider />
      <ModalFooter>
        <Flex marginLeft="auto" gap="1.2rem" px="1rem">
          <ModalAction
            id="add-onboarding-app-cancel"
            variant="text"
            onClick={closeModal}>
            {t('onboardings.addOnboardingAppModal.cancel')}
          </ModalAction>
          <ModalAction
            id="add-onboarding-app-submit"
            disabled={selectedApps.length === 0}
            onClick={handleAdd}>
            {t('onboardings.addOnboardingAppModal.addButton')}
          </ModalAction>
        </Flex>
      </ModalFooter>
    </ModalV2>
  );
};

export { AddOnboardingAppModal };
